import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, AlertCircle, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

type FeedbackType = 'success' | 'error' | 'warning' | 'info';

interface FeedbackProps {
  type: FeedbackType;
  message: string;
  isVisible: boolean;
  onClose: () => void;
  duration?: number;
}

const icons = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertCircle,
  info: Info,
};

const colors = {
  success: 'bg-green-50 text-green-800 border-green-200',
  error: 'bg-red-50 text-red-800 border-red-200',
  warning: 'bg-yellow-50 text-yellow-800 border-yellow-200',
  info: 'bg-blue-50 text-blue-800 border-blue-200',
};

export function Feedback({ type, message, isVisible, onClose, duration = 5000 }: FeedbackProps) {
  const Icon = icons[type];

  React.useEffect(() => {
    if (isVisible && duration) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className={`fixed top-4 right-4 z-50 p-4 rounded-lg border ${colors[type]} shadow-lg`}
        >
          <div className="flex items-center gap-3">
            <Icon className="w-5 h-5" />
            <p className="text-sm font-medium">{message}</p>
            <button onClick={onClose} className="ml-4 text-gray-400 hover:text-gray-600">
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Hook para usar o feedback
export function useFeedback() {
  const [feedback, setFeedback] = React.useState<{
    type: FeedbackType;
    message: string;
    isVisible: boolean;
  }>({
    type: 'info',
    message: '',
    isVisible: false,
  });

  const showFeedback = React.useCallback((type: FeedbackType, message: string) => {
    setFeedback({ type, message, isVisible: true });
  }, []);

  const hideFeedback = React.useCallback(() => {
    setFeedback((prev) => ({ ...prev, isVisible: false }));
  }, []);

  return {
    feedback,
    showFeedback,
    hideFeedback,
  };
}
